// orchestrator_fullstack/frontend/src/app/pages/ForbiddenPage.tsx
import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";

/**
 * ForbiddenPage
 * - Used by RequirePermission / route guards when user is authenticated but lacks permission.
 * - Shows current tenant + roles (from /v1/auth/me via AuthProvider).
 * - Provides a safe navigation back to admin overview or login.
 */
export default function ForbiddenPage(props: {
  required?: string[]; // permissions or roles required
  roles?: string[]; // optional override (e.g. from guard)
  tenant?: string | null; // optional override (e.g. from guard)
  message?: string;
}) {
  const nav = useNavigate();
  const loc = useLocation();
  const { state, flags, signOut } = useAuth();

  const effectiveTenant = props.tenant ?? state.tenant ?? null;

  const effectiveRoles = useMemo(() => {
    const r =
      props.roles ??
      (Array.isArray(state.roles) ? state.roles.map((x) => String(x)) : []);
    return Array.from(new Set(r.filter(Boolean)));
  }, [props.roles, state.roles]);

  const required = useMemo(() => {
    const r = props.required ?? [];
    return Array.from(new Set(r.map((x) => String(x)).filter(Boolean)));
  }, [props.required]);

  const detail = props.message || "You don’t have permission to access this page.";

  const isAuthed = Boolean(state.token && state.user);

  return (
    <div style={{ padding: 24, maxWidth: 860, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <h2 style={{ fontSize: 22, margin: 0 }}>Forbidden</h2>
        <span
          style={{
            fontSize: 12,
            padding: "4px 10px",
            borderRadius: 999,
            background: "rgba(0,0,0,0.06)",
          }}
        >
          403
        </span>
      </div>

      <p style={{ marginTop: 10, color: "#555" }}>{detail}</p>

      <div
        style={{
          marginTop: 16,
          border: "1px solid rgba(0,0,0,0.12)",
          borderRadius: 12,
          padding: 14,
          background: "rgba(0,0,0,0.02)",
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Context</div>

        <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", rowGap: 8, columnGap: 12 }}>
          <div style={{ color: "#666" }}>Path</div>
          <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
            {loc.pathname}
            {loc.search || ""}
          </div>

          <div style={{ color: "#666" }}>Authenticated</div>
          <div>{isAuthed ? "Yes" : "No"}</div>

          <div style={{ color: "#666" }}>User</div>
          <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
            {state.user || "(none)"}
          </div>

          <div style={{ color: "#666" }}>Tenant</div>
          <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
            {effectiveTenant || "(unset)"}
          </div>

          <div style={{ color: "#666" }}>Roles</div>
          <div>
            {effectiveRoles.length ? (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {effectiveRoles.map((r) => (
                  <span
                    key={r}
                    style={{
                      fontSize: 12,
                      padding: "4px 10px",
                      borderRadius: 999,
                      background: "rgba(0,0,0,0.06)",
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    }}
                  >
                    {r}
                  </span>
                ))}
              </div>
            ) : (
              <span style={{ color: "#666" }}>(none)</span>
            )}
          </div>

          {required.length > 0 && (
            <>
              <div style={{ color: "#666" }}>Required</div>
              <div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {required.map((p) => (
                    <span
                      key={p}
                      style={{
                        fontSize: 12,
                        padding: "4px 10px",
                        borderRadius: 999,
                        background: "rgba(176,0,32,0.08)",
                        color: "#8b0018",
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                      }}
                    >
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            onClick={() => nav("/app/admin/overview", { replace: true })}
            style={{ padding: "10px 14px", fontWeight: 700 }}
            disabled={!flags.isPlatformAdmin && !flags.isTenantAdmin && !flags.isOperator && !isAuthed}
            title="Go back to the Admin overview"
          >
            Go to Admin Overview
          </button>

          <button
            type="button"
            onClick={() => nav("/login", { replace: true })}
            style={{ padding: "10px 14px" }}
          >
            Go to Login
          </button>

          <button
            type="button"
            onClick={() => {
              signOut();
              nav("/login", { replace: true });
            }}
            style={{ padding: "10px 14px" }}
          >
            Sign out
          </button>
        </div>

        <div style={{ marginTop: 12, fontSize: 12, color: "#666" }}>
          Note: The UI hides/blocks actions for convenience, but the backend is the source of truth. If you believe you
          should have access, ask a platform-admin to grant the role and re-login.
        </div>
      </div>
    </div>
  );
}